/**
 * Einheitlicher, handgeschriebener API-Client für den Griffschrift-Konverter.
 *
 * Dies ist der EINZIGE HTTP-Stack des Frontends (bewusste Entscheidung, siehe
 * frontend_improvements.md A2): der generierte hey-api-Client (`@/generated`) liefert nur noch
 * die **Typen** (Request-/Response-Modelle), seine SDK-Services werden nicht mehr aufgerufen.
 *
 * Eigenschaften:
 * - Alle Aufrufe laufen über die gemeinsamen Helfer (`requestJson`/`requestRaw`) mit **einer**
 *   zentralen Fehler-Normalisierung ({@link extractApiErrorMessage}): FastAPI-`detail` (String
 *   oder Pydantic-ValidationError-Liste) wird in eine `Error.message` übersetzt, sodass die
 *   `error instanceof Error`-Guards der Aufrufer die Server-Message wirklich anzeigen.
 * - Jede Methode akzeptiert optional `{signal, timeoutMs}` ({@link RequestOptions}) – damit sind
 *   lange Aufrufe (OMR-Upload, Convert, Variants) abbrechbar und gegen Überholen geschützt
 *   (die Stores brechen den jeweiligen Vorgänger-Request ab, siehe useMusicUploadStore).
 * - Request-Bodies sind mit den generierten Modellen typisiert (falscher Key = Compile-Fehler).
 */

import type {
    ChordGripsRequest,
    ChordGripsResponse,
    NoteGripsRequest,
    NoteGripsResponse,
} from '@/components/harmonika/gripTypes';
// Laufzeit-Import (TS-Enum, kein reiner Typ): der Generator emittiert TargetModeEnum als enum.
import {TargetModeEnum} from '@/generated';
import type {
    AnalysisRequest,
    AnalysisResult,
    AssembleRequest,
    ConversionMode,
    ChordInferenceRequest,
    ChordInferenceSettings,
    ConversionRequest,
    ConversionSettings,
    ConversionStatusStore,
    ConvertVariantsRequest,
    ConvertVariantsResponse,
    DiatonicShiftRequest,
    DiatonicShiftSettings,
    FileUploadResponse,
    MidiFromXmlRequest,
    ModeChangeRequest,
    PdfUploadResponse,
    RebarRequest,
    RenderRequest,
    SettingsResponse,
    TranspositionRequest,
    TranspositionSuggestionsResult,
    ValidationError,
    VoiceMergeRequest,
    BassPreviewRequest,
    BassButtonTonesResponse,
    BassLayoutSettings,
    VoiceMergeSettings,
} from '@/generated';

// ============ Fehler-Normalisierung ============

/** Fehler mit normalisierter, anzeigbarer Message (Server-`detail` bzw. Fallback) + HTTP-Status. */
export class ApiError extends Error {
    constructor(message: string, readonly status?: number) {
        super(message);
        this.name = 'ApiError';
    }
}

function isValidationErrorList(value: unknown): value is ValidationError[] {
    return Array.isArray(value) && value.every(
        (item) => !!item && typeof item === 'object' && 'msg' in item,
    );
}

/**
 * Zentrale Fehler-Normalisierung: extrahiert aus einem Fehler-Payload (FastAPI-`detail` als
 * String ODER Pydantic-ValidationError-Liste), einem `Error` oder einem rohen String eine
 * anzeigbare Message. Alle catch-Blöcke der App können darüber routen.
 */
export function extractApiErrorMessage(payload: unknown, fallback: string): string {
    if (payload instanceof Error) return payload.message || fallback;
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const detail = (payload as { detail?: unknown }).detail;
        if (typeof detail === 'string' && detail.trim()) return detail;
        if (isValidationErrorList(detail)) {
            const msgs = detail.map((e) => e.msg).filter(Boolean);
            if (msgs.length) return msgs.join('; ');
        }
    }
    return fallback;
}

/** True für abgebrochene/ausgelaufene Requests (Abort/Timeout) – dann keine Fehler-Toast zeigen. */
export function isAbortError(error: unknown): boolean {
    return error instanceof DOMException &&
        (error.name === 'AbortError' || error.name === 'TimeoutError');
}

// ============ Request-Optionen ============

export interface RequestOptions {
    /** Externer Abbruch (z. B. „Vorgänger-Request überholen" in den Stores). */
    signal?: AbortSignal;
    /** Harte Obergrenze in ms; danach wird mit `TimeoutError` abgebrochen. */
    timeoutMs?: number;
}

/** Default-Timeout für lange Operationen (OMR, Convert, Variants, Render). */
const LONG_REQUEST_TIMEOUT_MS = 300_000;

// ============ Client ============

class APIClient {
    constructor(private readonly baseUrl?: string) {}

    private resolveUrl(path: string): string {
        if (!this.baseUrl) {
            return path;
        }

        const normalizedBase = this.baseUrl.endsWith('/')
            ? this.baseUrl.slice(0, -1)
            : this.baseUrl;
        return `${normalizedBase}${path}`;
    }

    /**
     * Gemeinsamer Kern aller Aufrufe: führt den fetch mit kombiniertem Abort-/Timeout-Signal aus
     * und wirft bei !ok einen {@link ApiError} mit normalisierter Server-Message.
     */
    private async requestRaw(
        path: string,
        init: RequestInit,
        failMsg: string,
        opts?: RequestOptions,
    ): Promise<Response> {
        const url = this.resolveUrl(path);

        // Abort-Komposition: externes Signal + optionales Timeout auf EIN internes Signal mappen
        // (manuell statt AbortSignal.any – breiter unterstützt und einfach aufzuräumen).
        const controller = new AbortController();
        const onExternalAbort = () => controller.abort(
            opts?.signal?.reason ?? new DOMException('Request abgebrochen', 'AbortError'),
        );
        if (opts?.signal) {
            if (opts.signal.aborted) onExternalAbort();
            else opts.signal.addEventListener('abort', onExternalAbort, {once: true});
        }
        const timer = opts?.timeoutMs
            ? setTimeout(
                () => controller.abort(new DOMException(
                    `Zeitüberschreitung nach ${Math.round(opts.timeoutMs! / 1000)}s`, 'TimeoutError',
                )),
                opts.timeoutMs,
            )
            : null;

        try {
            const response = await fetch(url, {...init, signal: controller.signal});
            if (!response.ok) {
                const payload = await response.json().catch(() => null);
                throw new ApiError(extractApiErrorMessage(payload, failMsg), response.status);
            }
            return response;
        } finally {
            if (timer) clearTimeout(timer);
            opts?.signal?.removeEventListener('abort', onExternalAbort);
        }
    }

    private async requestJson<T>(
        path: string,
        body: unknown,
        failMsg: string,
        opts?: RequestOptions,
    ): Promise<T> {
        const response = await this.requestRaw(
            path,
            {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
            },
            failMsg,
            opts,
        );
        return response.json() as Promise<T>;
    }

    // ============ Endpunkte ============

    /** Aktuelle Server-Default-Settings lesen (nur Erststart/Reset des Settings-Stores). */
    async getSettings(opts?: RequestOptions): Promise<SettingsResponse> {
        const response = await this.requestRaw(
            '/api/settings',
            {method: 'GET'},
            'Einstellungen konnten nicht geladen werden',
            opts,
        );
        return response.json() as Promise<SettingsResponse>;
    }

    /**
     * Upload einer MusicXML-/MIDI-Datei (multipart). Der Server verarbeitet den Inhalt stateless
     * und gibt das MusicXML inline zurück. PDFs laufen über {@link uploadPdf}.
     *
     * @param chordInference Gate + Settings der Upload-Akkorderkennung: Feld weggelassen =
     *   keine Erkennung (Re-Uploads lassen so einen vorhandenen Erkennungs-Vermerk unangetastet);
     *   mitgeschickt = der Server honoriert die Settings vollständig (der Aufrufer pinnt
     *   Schutzwerte wie `overwrite_existing: false` selbst).
     * @param bassLayout Bass-Belegung für `chordInference.restrict_to_bass_layout`; weggelassen =
     *   Server-Default-Layout.
     */
    async uploadFile(
        file: File,
        chordInference?: ChordInferenceSettings,
        opts?: RequestOptions,
        bassLayout?: BassLayoutSettings,
    ): Promise<FileUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);
        if (chordInference) formData.append('chord_inference', JSON.stringify(chordInference));
        if (bassLayout) formData.append('bass_layout', JSON.stringify(bassLayout));
        const response = await this.requestRaw(
            '/api/upload',
            {method: 'POST', body: formData},
            'Upload fehlgeschlagen',
            opts,
        );
        return response.json() as Promise<FileUploadResponse>;
    }

    /**
     * Analyze the given MusicXML content (inhaltsbasiert, stateless).
     */
    async analyzeFile(fileId: string, musicXml: string, opts?: RequestOptions): Promise<AnalysisResult> {
        const body: AnalysisRequest = {file_id: fileId, music_xml: musicXml};
        return this.requestJson<AnalysisResult>('/api/analyze', body, 'Analyse fehlgeschlagen', opts);
    }

    /**
     * Findet Transpositionsvorschläge unabhängig von der schnellen Basisanalyse.
     */
    async findTranspositionSuggestions(
        fileId: string,
        musicXml: string,
        opts?: RequestOptions,
    ): Promise<TranspositionSuggestionsResult> {
        const body: AnalysisRequest = {file_id: fileId, music_xml: musicXml};
        return this.requestJson<TranspositionSuggestionsResult>(
            '/api/transposition-suggestions',
            body,
            'Transpositionsvorschläge konnten nicht ermittelt werden',
            opts,
        );
    }

    /**
     * Griff + Alternativen für EIN Element einer Griffschrift-**Ausgabe** (inhaltsbasiert, stateless).
     * Liefert den aktuell verwendeten Griff (assigned) und alle spielbaren Alternativen, gerankt.
     */
    async noteGrips(
        musicXml: string,
        measureNumber: number,
        voiceNoteIndex: number,
        voice = 1,
        partIndex = 0,
        opts?: RequestOptions,
        measureIndex?: number,
    ): Promise<NoteGripsResponse> {
        const body: NoteGripsRequest = {
            music_xml: musicXml,
            measure_number: measureNumber,
            measure_index: measureIndex,
            voice_note_index: voiceNoteIndex,
            voice,
            part_index: partIndex,
        };
        return this.requestJson<NoteGripsResponse>(
            '/api/note-grips',
            body,
            'Griff konnte nicht ermittelt werden',
            opts,
        );
    }

    /**
     * Griffe eines Akkords (Grundton + Akkordtyp) auf Druck und Zug – der Akkord-Finder.
     * Ohne Partitur; `stimmung_offset` rechnet Stimmung/Transposition in den G-C-F-B-Raum.
     */
    async chordGrips(body: ChordGripsRequest, opts?: RequestOptions): Promise<ChordGripsResponse> {
        return this.requestJson<ChordGripsResponse>(
            '/api/chord-grips',
            body,
            'Griffe konnten nicht ermittelt werden',
            opts,
        );
    }

    /** Was jeder Bassknopf auf Druck und Zug klingt (MIDI je Knopf, wie das Layout angeordnet). */
    async bassButtonTones(bassLayout: BassLayoutSettings, opts?: RequestOptions): Promise<BassButtonTonesResponse> {
        return this.requestJson<BassButtonTonesResponse>(
            '/api/bass-button-tones',
            {bass_layout: bassLayout},
            'Bass-Töne konnten nicht ermittelt werden',
            opts,
        );
    }

    /**
     * Render raw MusicXML content to a binary artifact (PDF/PNG/MIDI) – stateless.
     * Ersetzt die früheren `download_urls`/`downloadUrl`-GET-Downloads: der Client schickt den
     * aktuellen Inhalt und erhält die Bytes zurück (kein serverseitiger Zustand).
     */
    async renderMusicXml(
        musicXml: string,
        format: 'pdf' | 'png' | 'midi',
        opts?: RequestOptions,
    ): Promise<Blob> {
        const body: RenderRequest = {music_xml: musicXml, format};
        const response = await this.requestRaw(
            '/api/render',
            {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
            },
            'Render fehlgeschlagen',
            {timeoutMs: LONG_REQUEST_TIMEOUT_MS, ...opts},
        );
        return response.blob();
    }

    /**
     * Transpose the given MusicXML content by a semitone delta (inhaltsbasiert, stateless).
     * Der Server transponiert den übergebenen Inhalt um `semitones` und liefert ihn inline zurück.
     */
    async transposeFile(
        fileId: string,
        semitones: number,
        musicXml: string,
        partIndices?: number[],
        opts?: RequestOptions,
    ): Promise<FileUploadResponse> {
        const body: TranspositionRequest = {file_id: fileId, semitones, music_xml: musicXml};
        if (partIndices && partIndices.length) body.part_indices = partIndices;
        return this.requestJson<FileUploadResponse>(
            '/api/transpose', body, 'Transposition fehlgeschlagen', opts,
        );
    }

    /**
     * Variantwechsel (Dur↔Moll) auf den übergebenen MusicXML-Inhalt anwenden (stateless).
     * Erst der Stufen-Remap in die Varianttonart, danach optional `semitones` chromatisch.
     */
    async modeChange(
        fileId: string,
        musicXml: string,
        targetMode: 'major' | 'minor',
        semitones = 0,
        opts?: RequestOptions,
    ): Promise<FileUploadResponse> {
        const body: ModeChangeRequest = {
            file_id: fileId,
            target_mode: targetMode === 'major' ? TargetModeEnum.Major : TargetModeEnum.Minor,
            semitones,
            music_xml: musicXml,
        };
        return this.requestJson<FileUploadResponse>(
            '/api/mode-change', body, 'Variantwechsel fehlgeschlagen', opts,
        );
    }

    /**
     * Taktart ab Takt-Index `startMeasureIndex` ändern und die Noten neu auf Takte verteilen
     * (stateless). Hinweise des Servers (z. B. entfallene Wiederholungen) stehen in `message`.
     */
    async rebar(
        fileId: string,
        musicXml: string,
        beats: number,
        beatType: number,
        startMeasureIndex = 0,
        opts?: RequestOptions,
    ): Promise<FileUploadResponse> {
        const body: RebarRequest = {
            file_id: fileId,
            beats,
            beat_type: beatType,
            start_measure_index: startMeasureIndex,
            music_xml: musicXml,
        };
        return this.requestJson<FileUploadResponse>(
            '/api/rebar', body, 'Taktart ändern fehlgeschlagen', opts,
        );
    }

    /**
     * Apply diatonic shift to the given MusicXML content (inhaltsbasiert, reihenfolgeunabhängig).
     * Die file_id dient serverseitig nur als opakes Label.
     */
    async shiftFile(
        fileId: string,
        settings: DiatonicShiftSettings,
        musicXml: string,
        opts?: RequestOptions,
    ): Promise<FileUploadResponse> {
        const body: DiatonicShiftRequest = {file_id: fileId, settings, music_xml: musicXml};
        return this.requestJson<FileUploadResponse>(
            '/api/diatonic-shift', body, 'Diatonische Verschiebung fehlgeschlagen', opts,
        );
    }

    /**
     * Merge voices/parts of the given MusicXML content (inhaltsbasiert, reihenfolgeunabhängig).
     * Die file_id dient serverseitig nur als opakes Label.
     */
    async mergeVoices(
        fileId: string,
        settings: VoiceMergeSettings,
        musicXml: string,
        opts?: RequestOptions,
    ): Promise<FileUploadResponse> {
        const body: VoiceMergeRequest = {file_id: fileId, settings, music_xml: musicXml};
        return this.requestJson<FileUploadResponse>(
            '/api/merge-voices', body, 'Stimmen-Zusammenführen fehlgeschlagen', opts,
        );
    }

    /**
     * Add or remove the sounding bass preview part (Bass-System im Bassschlüssel).
     *
     * Zeigt vor der Konvertierung, welchen Wechselbass die Harmonika zu den erkannten Akkorden
     * spielen würde – reine Prüfhilfe, die Analyse und Konvertierung ausklammern.
     */
    async bassPart(
        fileId: string,
        layout: BassLayoutSettings,
        musicXml: string,
        remove = false,
        measureStyles?: Record<string, string>,
        opts?: RequestOptions,
    ): Promise<FileUploadResponse> {
        // `measure_styles` weglassen (nicht `{}`) heißt serverseitig: die im Score vermerkten
        // Spielweisen behalten. Ein leeres Objekt würde sie löschen.
        const body: BassPreviewRequest = {
            file_id: fileId, layout, music_xml: musicXml, remove,
            ...(measureStyles ? {measure_styles: measureStyles} : {}),
        };
        return this.requestJson<FileUploadResponse>(
            '/api/bass-part',
            body,
            remove ? 'Bass-System entfernen fehlgeschlagen' : 'Bass-System erzeugen fehlgeschlagen',
            opts,
        );
    }

    /**
     * (Re-)apply chord-symbol detection to the given MusicXML content (inhaltsbasiert,
     * reihenfolgeunabhängig). Entfernt zuvor automatisch erkannte (markierte) Akkorde und erkennt
     * mit den übergebenen Settings neu. ``settings.overwrite_existing`` entfernt zusätzlich
     * originale/vom User gesetzte Symbole (Standard: nur die auto-erkannten werden neu bestimmt).
     */
    async inferChordSymbols(
        fileId: string,
        settings: ChordInferenceSettings,
        musicXml: string,
        opts?: RequestOptions,
        layout?: BassLayoutSettings,
    ): Promise<FileUploadResponse> {
        const body: ChordInferenceRequest = {
            file_id: fileId,
            settings,
            music_xml: musicXml,
            ...(layout ? {layout} : {}),
        };
        return this.requestJson<FileUploadResponse>(
            '/api/chord-inference', body, 'Akkorderkennung fehlgeschlagen', opts,
        );
    }

    /**
     * Generate MIDI bytes from raw MusicXML content (stateless) – für die Vorschau-Wiedergabe.
     */
    async fetchMidiFromXml(musicXml: string, opts?: RequestOptions): Promise<ArrayBuffer> {
        const body: MidiFromXmlRequest = {music_xml: musicXml};
        const response = await this.requestRaw(
            '/api/midi',
            {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
            },
            'MIDI konnte nicht erzeugt werden',
            opts,
        );
        return response.arrayBuffer();
    }

    /**
     * Convert the given MusicXML content to Griffschrift (inhaltsbasiert). Der übergebene Inhalt
     * ist bereits final (Transposition/Shift eingebacken); die file_id ist nur ein opakes Label.
     */
    async convertFile(args: {
        fileId: string;
        musicXml: string;
        mode: ConversionMode;
        settings: ConversionSettings;
        /** Dateiname für den Titel des Ergebnis-Scores (sonst Fallback 'Griffschrift'). */
        originalFilename?: string;
    }, opts?: RequestOptions): Promise<ConversionStatusStore> {
        const body: ConversionRequest = {
            file_id: args.fileId,
            music_xml: args.musicXml,
            mode: args.mode,
            settings: args.settings,
        };
        if (args.originalFilename) body.original_filename = args.originalFilename;
        return this.requestJson<ConversionStatusStore>(
            '/api/convert', body, 'Konvertierung fehlgeschlagen',
            {timeoutMs: LONG_REQUEST_TIMEOUT_MS, ...opts},
        );
    }

    /** Per-Measure: alle Taktvarianten aus dem übergebenen MusicXML-Inhalt berechnen. */
    async convertVariants(args: {
        fileId: string;
        musicXml: string;
        settings: ConversionSettings;
    }, opts?: RequestOptions): Promise<ConvertVariantsResponse> {
        const body: ConvertVariantsRequest = {
            file_id: args.fileId,
            music_xml: args.musicXml,
            settings: args.settings,
        };
        return this.requestJson<ConvertVariantsResponse>(
            '/api/convert/variants', body, 'Variantengenerierung fehlgeschlagen',
            {timeoutMs: LONG_REQUEST_TIMEOUT_MS, ...opts},
        );
    }

    /** Per-Measure: finalen Score aus der Auswahl + (eingefrorenem) Inhalt zusammensetzen. */
    async convertAssemble(args: {
        fileId: string;
        musicXml: string;
        settings: ConversionSettings;
        perMeasureSelection: string[];
        /** Dateiname für den Titel des Ergebnis-Scores (sonst Fallback 'Griffschrift'). */
        originalFilename?: string;
    }, opts?: RequestOptions): Promise<ConversionStatusStore> {
        const body: AssembleRequest = {
            file_id: args.fileId,
            music_xml: args.musicXml,
            settings: args.settings,
            per_measure_selection: args.perMeasureSelection,
        };
        if (args.originalFilename) body.original_filename = args.originalFilename;
        return this.requestJson<ConversionStatusStore>(
            '/api/convert/assemble', body, 'Zusammensetzen fehlgeschlagen',
            {timeoutMs: LONG_REQUEST_TIMEOUT_MS, ...opts},
        );
    }

    /**
     * Upload a PDF file for OMR conversion using Audiveris
     * This endpoint may take longer as it performs optical music recognition.
     *
     * @param improveImage Wenn true, wird die PDF vor der OMR zu einem hochauflösenden,
     *   gesäuberten/verdickten Bild aufbereitet (hilft bei verrauschten Scans).
     * @param removeBass Wenn true, entfernt der Server bei mehrteiligen Movements die
     *   Bass-Schlüssel-Parts. Default false = der Bass bleibt in der Arbeits-/Vorschaudatei
     *   erhalten (der Nutzer sieht die volle Datei; die Griffschrift-Konvertierung ist ohnehin
     *   monophon).
     * @param chordInference Gate + Settings der Akkorderkennung je Movement – Semantik wie bei
     *   {@link uploadFile} (Feld weggelassen = keine Erkennung).
     *
     * Liefert ein {@link PdfUploadResponse} mit einem Eintrag pro Movement (mehrteilige PDFs →
     * mehrere Movements; einteilige → genau einer).
     */
    async uploadPdf(
        file: File,
        improveImage = false,
        removeBass = false,
        chordInference?: ChordInferenceSettings,
        opts?: RequestOptions,
        bassLayout?: BassLayoutSettings,
    ): Promise<PdfUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('improve_image', String(improveImage));
        formData.append('remove_bass_parts', String(removeBass));
        if (chordInference) formData.append('chord_inference', JSON.stringify(chordInference));
        if (bassLayout) formData.append('bass_layout', JSON.stringify(bassLayout));

        const response = await this.requestRaw(
            '/api/upload-pdf',
            {method: 'POST', body: formData},
            'PDF-Konvertierung fehlgeschlagen',
            {timeoutMs: LONG_REQUEST_TIMEOUT_MS, ...opts},
        );
        return response.json() as Promise<PdfUploadResponse>;
    }
}

// Create and export global API client instance
export const apiClient = new APIClient();

// Also export the class for creating new instances if needed
export {APIClient};

// Export types for external use
export type * from './generated';
